import { Suspense, lazy, useEffect, useState, useSyncExternalStore } from 'react';
import { Download } from '../../../icons';
import { Button } from '../../../components/shared';
import { DocPanel } from '../../../components/DocPanel/DocPanel';
import { fetchAttachmentText, type PreviewDoc } from '../../../lib/preview-doc';
import { tBoards, getBoardsLang, subscribeBoardsLang } from '../../../lib/boards-i18n';

/**
 * 첨부 문서 패널 — 리더 오른쪽에 붙는다 (시안 07, 2026-08-18).
 *
 * 기존 `BoardDocViewer` 는 `.bd-viewer` 로 **화면을 덮는 오버레이**다. 규정·공고는 본문과
 * 첨부를 나란히 보며 읽으므로, 여기서는 덮지 않고 옆에 편다.
 *
 * 렌더러는 채팅 미리보기의 것을 그대로 쓴다(수정 0). 껍데기는 공용 `DocPanel` 이라
 * 헤더(제목·내려받기·펼치기·닫기)와 Esc 닫기가 공짜로 따라온다.
 *
 * ⚠ 기존 `BoardDocViewer` 는 한 줄도 건드리지 않는다 — 플래그 OFF 경로가 그 파일을 쓴다.
 */

const DocxPreview = lazy(() => import('../../ChatPage/ChatArea/preview/DocxPreview'));
const XlsxPreview = lazy(() => import('../../ChatPage/ChatArea/preview/XlsxPreview'));
const PptxPreview = lazy(() => import('../../ChatPage/ChatArea/preview/PptxPreview'));
const PdfPreview = lazy(() => import('../../ChatPage/ChatArea/preview/PdfPreview'));
const HwpPreview = lazy(() => import('../../ChatPage/ChatArea/preview/HwpPreview'));

interface BoardDocPanelProps {
  doc: PreviewDoc;
  /** 전체 폭으로 펼쳤는가(리더 본문을 밀어낸다). */
  expanded: boolean;
  onToggleExpand: () => void;
  onDownload: () => void;
  onClose: () => void;
}

export function BoardDocPanel({ doc, expanded, onToggleExpand, onDownload, onClose }: BoardDocPanelProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const isText = doc.kind === 'markdown' || doc.kind === 'html';

  return (
    <aside className={`bdw-rpanel${expanded ? ' bdw-rpanel--wide' : ''}`}>
      <DocPanel
        title={doc.title}
        expanded={expanded}
        onExpand={onToggleExpand}
        onClose={onClose}
        actions={(
          <Button
            variant="ghost" size="sm" iconOnly
            aria-label={doc.title}
            title={tBoards('boards.btn.download')}
            onClick={onDownload}
            iconLeft={<Download size={14} aria-hidden />}
          />
        )}
      >
        <Suspense fallback={<p className="bd-muted">{tBoards('boards.viewer.loading')}</p>}>
          {doc.kind === 'pdf' && <PdfPreview doc={doc} />}
          {doc.kind === 'docx' && <DocxPreview doc={doc} />}
          {doc.kind === 'xlsx' && <XlsxPreview doc={doc} />}
          {doc.kind === 'pptx' && <PptxPreview doc={doc} />}
          {doc.kind === 'hwp' && <HwpPreview doc={doc} />}
          {isText && <TextDoc doc={doc} />}
        </Suspense>
      </DocPanel>
    </aside>
  );
}

/**
 * markdown/html 은 원문을 그대로 보여 준다 — `innerHTML` 로 붙이지 않는 것이 요점이다
 * (게시판 첨부는 누구나 올린다). 기존 뷰어와 같은 규칙.
 */
function TextDoc({ doc }: { doc: PreviewDoc }) {
  const fileId = doc.source.kind === 'attachment' ? doc.source.fileId : null;
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setText(null);
    fetchAttachmentText(fileId)
      .then((t) => { if (!cancelled) setText(t); })
      .catch(() => { if (!cancelled) setText(''); });
    return () => { cancelled = true; };
  }, [fileId]);

  if (text === null) return <p className="bd-muted">{tBoards('boards.viewer.loading')}</p>;
  return <pre className="bd-viewer__text">{text}</pre>;
}
